import { useDispatch, useSelector } from "react-redux";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { useForm } from "antd/lib/form/Form";
import { Row, Col, Modal, Button, Space, Drawer } from "antd";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RootState } from "../../shared/types/common";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  FileTextOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";

import CreateEditDocumentSubTypeComponent from "./create-edit-document-sub-type.component";

import {
  deleteDocumentSubType,
  getDocumentSubTypeList,
} from "../../core/store/reducers/document-sub-type.slice";

import { TABLE_ROWS_COUNT } from "../../shared/constants";

import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";

import {
  accessAllowed,
  showNotification,
} from "../../shared/utils/utils";

import { allPermissions } from "../../shared/constants/constants";

import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";

import { isFilterValueActive } from "../../shared/utils/active-filters";

import { Client } from "../../core/api-client";

import { API_GET_ALL_DOCUMENT_TYPE_ACTIVELIST, API_GET_ALL_DOCUMENT_TYPE_LIST } from "../../shared/routes/private.routes";

const DOCUMENT_SUB_TYPE_FILTER_LABELS: Record<string, string> = {
  documentSubTypeName: "Name",
  documentTypeId: "Document Type",
  status: "Status",
};

function documentSubTypeFilterValueText(
  key: string,
  value: any,
  documentTypeList: any[]
): string {
  switch (key) {
    case "documentSubTypeName":
      return String(value).trim();

    case "status":
      return Array.isArray(value)
        ? value.join(", ")
        : String(value);

    case "documentTypeId": {
      const documentType = documentTypeList.find(
        (item: any) => item.id === value
      );

      return documentType
        ? documentType.documentTypeName || documentType.name
        : String(value);
    }

    default:
      return String(value);
  }
}

const DOCUMENT_SUB_TYPE_FILTER_CLEAR: Record<string, any> = {
  documentSubTypeName: undefined,
  documentTypeId: undefined,
  status: [],
};

const ManageDocumentSubTypePage: React.FC = () => {
  const dispatch = useDispatch();

  const [form] = useForm();

  const { screenType } = useSelector(
    (state: RootState) => state.settings
  );

  const { myPermissions } = useSelector(
    (state: RootState) => state.auth
  );

  const {
    loadingDocumentSubType,
    documentSubTypeList,
    pagination= { page: 1, total: 0 } } = useSelector(
    (state: RootState) => state.documentSubType
  );

  const [documentSubTypeListData, setDocumentSubTypeListData] =
    useState([] as any);

  const [documentTypeList, setDocumentTypeList] =
    useState([] as any);

  const [documentSubTypeId, setDocumentSubTypeId] =
    useState("");

  const [deleteModalVisible, setDeleteModalVisible] =
    useState(false);

  const [sorter, setSorter] = useState({
    sort_by: "",
    sort_type: "",
  });

  const [filters, setFilters] = useState({} as any);

  const [createEditOpen, setCreateEditOpen] =
    useState(false);

  const [selectedEntity, setSelectedEntity] =
    useState(null as any);

  const [filterVisible, setFilterVisible] =
    useState(false);

  const [openLogs, setOpenLogs] =
    useState(false);

  const [pageSize, setPageSize] =
    useState(TABLE_ROWS_COUNT);

  /**
   * Load Document Sub Type list
   */
  useEffect(() => {
    loadList(1);
  }, [filters, sorter, pageSize]);

  const loadList = (page: any = null) => {
    const data = {
      page: page
        ? page
        : pagination?.page
        ? pagination.page
        : 1,
      limit: pageSize,
      ...sorter,
    };

    const filter = { ...filters };

    dispatch(
      getDocumentSubTypeList({
        params: data,
        body: filter,
      })
    );
  };

  /**
   * Load Document Type list for dropdown
   */
  useEffect(() => {
    loadDocumentTypeList();
  }, []);

  const loadDocumentTypeList = async () => {
    try {
      const response: any = await Client.get(
        API_GET_ALL_DOCUMENT_TYPE_ACTIVELIST
      );

      const list =
        response?.data?.data ||
        response?.data ||
        [];

      setDocumentTypeList(
        Array.isArray(list) ? list : []
      );
    } catch (error) {
      console.error(
        "Failed to load document type list",
        error
      );
    }
  };

  /**
   * Create
   */
  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };

  /**
   * Edit
   */
  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };

  /**
   * Filter apply
   */
  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  /**
   * Active filter chips
   */
  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) =>
        isFilterValueActive(filters[k])
      )
      .map((key) => ({
        key,
        fieldLabel:
          DOCUMENT_SUB_TYPE_FILTER_LABELS[key] ||
          key,
        valueText:
          documentSubTypeFilterValueText(
            key,
            filters[key],
            documentTypeList
          ),
      }));
  }, [filters, documentTypeList]);

  /**
   * Clear all filters
   */
  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  /**
   * Remove single filter
   */
  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      const cleared =
        key in DOCUMENT_SUB_TYPE_FILTER_CLEAR
          ? DOCUMENT_SUB_TYPE_FILTER_CLEAR[key]
          : undefined;

      form.setFieldsValue({
        [key]: cleared,
      });
    },
    [form]
  );

  /**
   * Pagination
   */
  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };

  const onPageSizeChange = (
    _pageNumber: number,
    selectedPageSize: number
  ) => {
    setPageSize(selectedPageSize);
  };

  /**
   * Set table data
   */
  useEffect(() => {
    setDocumentSubTypeListData(
      documentSubTypeList.map(
        (obj: any, index: number) => {
          return {
            key: index,
            ...obj,
          };
        }
      )
    );
  }, [documentSubTypeList]);

  /**
   * Permissions
   *
   * If these permission constants have different names
   * in your project, replace them with your actual names.
   */
  const hasAnyDocumentSubTypeAccess =
    [
      allPermissions.CREATE_DOCUMENT_SUB_TYPE,
      allPermissions.UPDATE_DOCUMENT_SUB_TYPE,
      allPermissions.DELETE_DOCUMENT_SUB_TYPE,
      allPermissions.DOCUMENT_SUB_TYPE_ACTIVITY_LOGS,
    ].some((p) => accessAllowed(myPermissions, p));

  /**
   * Table columns
   */
  const columns = [
    {
      title: "Document Sub Type Name",
      dataIndex: "documentSubTypeName",
      sorter: false,
      key: "documentSubTypeName",
    },

    {
      title: "Document Type Name",
      dataIndex: "documentTypeName",
      sorter: false,
      key: "documentTypeName",
    },

    // Document Type column removed: sub-types are an independent, flat list and
    // are no longer owned by a parent document type.

    {
      title: "Status",
      dataIndex: "status",
      sorter: false,
      key: "status",
    },

    ...(hasAnyDocumentSubTypeAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",

            render: (
              text: any,
              record: any
            ) => (
              <Space>
                {accessAllowed(
                  myPermissions,
                  allPermissions.UPDATE_DOCUMENT_SUB_TYPE
                ) && (
                  <Button
                    type="link"
                    onClick={() =>
                      editEntityListner(record)
                    }
                    icon={<EditOutlined />}
                  />
                )}

                {accessAllowed(
                  myPermissions,
                  allPermissions.DELETE_DOCUMENT_SUB_TYPE
                ) && (
                  <Button
                    type="link"
                    onClick={() =>
                      handleDeleteModal(
                        record?.id
                      )
                    }
                    icon={
                      <DeleteOutlined
                        style={{
                          color: "#FF0000",
                        }}
                      />
                    }
                  />
                )}

                {accessAllowed(
                  myPermissions,
                  allPermissions.DOCUMENT_SUB_TYPE_ACTIVITY_LOGS
                ) && (
                  <Button
                    type="link"
                    onClick={() =>
                      handleOpenLogs(record)
                    }
                    icon={<EyeOutlined />}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  /**
   * Delete
   */
  
   const handleDelete = async (id: string) => {
    try {
      const result = await dispatch(deleteDocumentSubType(id) as any);
  
      if (result?.payload?.data) {
        showNotification("Document Type Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false);
      }
    } catch (error) {
      console.error("Delete Document Type failed:", error);
      setDeleteModalVisible(false);
    }
  };
  const handleDeleteModal = (
    id: string
  ) => {
    setDeleteModalVisible(true);
    setDocumentSubTypeId(id);
  };

  const onDeleteConfirm = () => {
    handleDelete(documentSubTypeId);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDocumentSubTypeId("");
  };

  /**
   * Activity logs
   */
  const handleOpenLogs = (
    record: any
  ) => {
    setOpenLogs(true);
    setDocumentSubTypeId(record?.id);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setDocumentSubTypeId("");
  };

  /**
   * Form value change
   */
  const onValuesChange = (
    _value: any,
    _allValues: any
  ) => {};

  /**
   * Filter form
   */
  const formProps = {
    form: form,

    name: "manage-document-sub-type-filters",

    enableEnterSubmit: true,

    formElements: [
      {
        name: "documentSubTypeName",
        label: "",
        placeholder:
          "Document Sub Type Name Search",
        type: "Input",
        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 4,
        },
      },

      // Document Type filter removed: sub-types no longer belong to a document
      // type, so new rows have a null documentTypeId and this filter matched
      // nothing.

      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",
        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 4,
        },
        showSearch: true,
        allowClear: true,
        mode: "multiple",

        options: [
          {
            value: "ACTIVE",
            label: "ACTIVE",
          },
          {
            value: "INACTIVE",
            label: "INACTIVE",
          },
        ],
      },
    ],

    initialValues: {
      documentSubTypeName: "",
      documentTypeId: undefined,
      status: [],
    },

    extraButtons: [
      {
        htmlType: "submit",

        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 3,
        },

        loading: false,

        submitButtonText: "Clear",

        type: "clearForm",

        onClick: clearAllFilters,
      },

      {
        htmlType: "submit",

        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 3,
        },

        loading: false,

        submitButtonText: "Apply",

        submitButtonType: "primary",
      },
    ],

    onFinish: onFilterApply,

    onFinishFailed: null,

    onValuesChange: onValuesChange,
  };

  /**
   * Header buttons
   */
  const headerButtons = (
    <Space
      size="middle"
      className="app-page-actions"
    >
      {accessAllowed(
        myPermissions,
        allPermissions.CREATE_DOCUMENT_SUB_TYPE
      ) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New{" "}
            {!screenType?.isPortrait &&
              "Document Sub Type"}
          </span>
        </Button>
      )}

      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() =>
          setFilterVisible(
            !filterVisible
          )
        }
        title={
          filterVisible
            ? "Hide filters"
            : "Show filters"
        }
      />
    </Space>
  );

  /**
   * Create/Edit success
   */
  const createEditSubmit = () => {
    setCreateEditOpen(false);
    setSelectedEntity(null);
    loadList();
  };

  /**
   * Sorting
   */
  const onChangeHandler = (
    _pagination: any,
    _filters: any,
    _sorter: any
  ) => {
    if (_sorter.order === undefined) {
      setSorter({
        sort_by: "",
        sort_type: "",
      });
    } else {
      let order = "";

      if (_sorter.order === "ascend") {
        order = "asc";
      } else {
        order = "desc";
      }

      let columnKey = "";

      if (
        _sorter.columnKey === "enabled"
      ) {
        columnKey = "status";
      } else if (
        _sorter.columnKey === "displayName"
      ) {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }

      setSorter({
        sort_by: columnKey,
        sort_type: order,
      });
    }
  };

  return (
    <div className="app-page">

      {/* Header */}
      <div className="app-page-header">
        <Row
          align="middle"
          justify="space-between"
          wrap
        >
          <Col
            flex="1"
            style={{ minWidth: 0 }}
          >
            <div className="app-page-title-wrap">

              <div className="app-page-icon">
                <FileTextOutlined />
              </div>

              <h1 className="app-page-title">
                Manage Document Sub Type
              </h1>

            </div>

            <p className="app-page-subtitle">
              View and manage document sub types
            </p>
          </Col>

          <Col>
            {headerButtons}
          </Col>
        </Row>
      </div>

      {/* Filters */}
      {filterVisible && (
        <div className="app-filter-bar">

          <div className="app-filter-header">

            <div className="app-filter-title">
              <BiFilterAlt
                style={{ fontSize: 14 }}
              />
              Filters
            </div>

            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() =>
                setFilterVisible(false)
              }
              title="Close filters"
              aria-label="Close filters"
            />

          </div>

          <div className="table-filter-form">
            <GenericFormComponent
              {...formProps}
            />
          </div>

        </div>
      )}

      {/* Active filter chips */}
      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      {/* Table */}
      <div className="app-table-card">

        <DataTableComponent
          pagination={true}
          loading={loadingDocumentSubType}
          dataSource={
            documentSubTypeListData
          }
          columns={columns}
          onChange={onChangeHandler}
          defaultPage={0}
          currentPage={pagination.page}
          totalRows={pagination?.total}
          onPageChange={onPageChange}
          onPageSizeChange={
            onPageSizeChange
          }
          pageSize={pageSize}
        />

      </div>

      {/* Delete Modal */}
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
      >
        <p>
          Are you sure you want to delete
          this document sub type?
        </p>
      </Modal>

      {/* Create / Edit */}
      {createEditOpen && (
        <CreateEditDocumentSubTypeComponent
          onClose={() =>
            setCreateEditOpen(false)
          }
          visible={createEditOpen}
          selectedEntity={
            selectedEntity
          }
          onSubmit={createEditSubmit}
          documentTypeList={
            documentTypeList
          }
        />
      )}

      {/* Activity Logs */}
      {openLogs && (
        <Drawer
          open={openLogs}
          onClose={onCloseLogs}
          title="Log Details"
        >
          <ActivityLogComponent
            id={
              selectedEntity?.id ||
              documentSubTypeId
            }
            record={selectedEntity}
            module="Document Sub Type"
          />
        </Drawer>
      )}

    </div>
  );
};

export default ManageDocumentSubTypePage;